import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  DefaultDadoField,
  UserImportDataSourceAdpWorkforceNow,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { useState } from 'react';
import shortid from 'shortid';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { usePromise } from '../../../../../../hooks/usePromise';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import {
  getAdpWorkforceNowFields,
  useAdpWorkforceNowConfig
} from '../../../../../Org/pages/Integrations/integrations/adp/service';
import { CustomImportMappingFormWithAutocomplete } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem, IntegrationNotConfigured } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.ADP_WORKFORCE_NOW;

const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: DefaultDadoField.FIRST_NAME,
    otherKey: 'person.legalName.givenName'
  },
  {
    dadoKey: DefaultDadoField.LAST_NAME,
    otherKey: 'person.legalName.familyName1'
  },
  {
    dadoKey: DefaultDadoField.EMAIL,
    otherKey: 'emails.primaryWorkEmail'
  },
  {
    dadoKey: DefaultDadoField.HIRE_DATE,
    otherKey: 'workerDates.originalHireDate'
  },
  {
    dadoKey: DefaultDadoField.MANAGER_ID,
    otherKey: 'workAssignment.reportsTo.0.associateOID'
  }
];

export const AdpWorkforceNowSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { id: userId } = useCurrentUser();
  const [config, loading, error] = useAdpWorkforceNowConfig(orgId, userId);
  const isSetup = !!(config?.data.clientId && config?.data.clientSecret);
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [mappings, setMappings] = useState<UserImportMapping[]>(
    DEFAULT_MAPPINGS
  );

  const [adpWorkforceNowFields, loadingFields, errorFields] = usePromise(
    async () => {
      const result = await getAdpWorkforceNowFields(orgId);
      return result;
    }
  );

  if (loading || loadingFields) {
    return (
      <DialogContent>
        <Loader height={250} />
      </DialogContent>
    );
  }
  if (error || errorFields) {
    return <SomethingWentWrong height={150} />;
  }
  if (!config) {
    return null;
  }

  if (!isSetup) {
    return (
      <IntegrationNotConfigured integration={META.name} onClose={onClose} />
    );
  }

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>

        {adpWorkforceNowFields && (
          <CustomImportMappingFormWithAutocomplete
            userFields={userFields}
            integrationName={META.name}
            name={t('Default Field Mapping')}
            mappings={mappings}
            onChange={(mappings) => setMappings(mappings)}
            fields={adpWorkforceNowFields.map((f) => ({
              name: f.fieldPath,
              label: f.label
            }))}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            const nextSource: Doc<UserImportDataSourceAdpWorkforceNow> = {
              id: shortid(),
              collection: FS.userImportDataSources,
              data: {
                orgId: orgId,
                type: UserImportSourceType.ADP_WORKFORCE_NOW,
                status: UserImportDataSourceStatus.PENDING,
                detailedStatus: '',
                lastUpdate: null,
                createdBy: userId,
                createdAt: now(),
                customMappings: mappings.filter((m) => !!m.otherKey)
              }
            };
            await setDoc(nextSource);
            onClose();
          }}
        >
          {t('Add source')}
        </Button>
      </DialogActions>
    </>
  );
};

export const ImportSourceRowAdpWorkForceNow = ({
  d
}: {
  d: Doc<UserImportDataSourceAdpWorkforceNow>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
